import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Dialog } from "@mui/material";
import type { DialogProps } from "@mui/material/Dialog";
import clsx from "clsx";
import { createPortal } from "react-dom";

import { roobert } from "@musicfy/assets/fonts";

export interface IFullScreenTakeoverProps extends DialogProps {
  children?: React.ReactNode;
  open: boolean;
  onClose?: () => void;
  title?: string;
  /**
   * Cover the main content + right sidebar, but leave the real left/top nav
   * visible and clickable. Prefer this for pickers opened from Create nav links.
   */
  keepSidebarVisible?: boolean;
}

const MAIN_CONTENT_OVERLAY_ROOT_ID = "main-content-overlay-root";

const TakeoverChrome: React.FC<{
  onClose?: () => void;
  title?: string;
  closeAlign: "start" | "end";
  children?: React.ReactNode;
}> = ({ onClose, title, closeAlign, children }) => {
  return (
    <>
      <div
        role="button"
        aria-label="Close Modal"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClose?.();
          }
        }}
        className={clsx(
          "flex xl:sticky xl:m-0 mx-6 mt-4 z-30 top-8 h-10 p-2 w-10 cursor-pointer items-center justify-center rounded-full border border-white/15 xl:hover:border-white/30 bg-white/5 transition-all xl:hover:bg-white/20",
          closeAlign === "start"
            ? "self-start xl:left-8"
            : "self-end xl:right-8"
        )}
      >
        <CloseIcon className="!text-22 !h-full !w-full" />
      </div>
      <div className="relative mx-auto w-full p-6 xl:max-w-screen-xxl xl:p-9">
        {!!title && (
          <div className="z-20 flex items-center justify-between xl:pt-6 pb-6">
            <div className="truncate text-32 font-bold capitalize">{title}</div>
          </div>
        )}
        {children}
      </div>
    </>
  );
};

const FullScreenTakeover: React.FC<IFullScreenTakeoverProps> = ({
  children,
  open,
  onClose,
  title,
  keepSidebarVisible = false,
  ...dialogProps
}) => {
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!keepSidebarVisible) {
      return;
    }
    setOverlayRoot(document.getElementById(MAIN_CONTENT_OVERLAY_ROOT_ID));
  }, [keepSidebarVisible]);

  useEffect(() => {
    if (!keepSidebarVisible || !open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [keepSidebarVisible, open, onClose]);

  // Content-area overlay: spans main + right sidebar, leaves left nav alone.
  if (keepSidebarVisible) {
    if (!open || !overlayRoot) {
      return null;
    }

    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        className={clsx(
          roobert.variable,
          "pointer-events-auto absolute inset-0 flex flex-col overflow-y-auto bg-gray-1000/95 backdrop-blur-2xl text-white font-sans"
        )}
      >
        <TakeoverChrome onClose={onClose} title={title} closeAlign="start">
          {children}
        </TakeoverChrome>
      </div>,
      overlayRoot
    );
  }

  return (
    <Dialog
      {...dialogProps}
      fullScreen
      open={open}
      onClose={onClose}
      className={`${roobert.variable} font-sans`}
      PaperProps={{
        className: "!bg-black/5 !backdrop-blur-2xl !text-white",
        style: {
          boxShadow: "none",
        },
      }}
    >
      <TakeoverChrome onClose={onClose} title={title} closeAlign="end">
        {children}
      </TakeoverChrome>
    </Dialog>
  );
};

export default FullScreenTakeover;
